"use client";

import { AnimatePresence, motion } from "motion/react";
import { useState, useEffect } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// Utility function for combining class names
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const CheckIcon = ({
  className
}) => {
  return (
    <div 
      style={{ 
        width: '20px', 
        height: '20px', 
        borderRadius: '50%', 
        border: '2px solid rgb(0, 0, 0)',
        backgroundColor: 'transparent'
      }}
      className={cn("", className)}>
    </div>
  );
};

const CheckFilled = ({
  className
}) => {
  return (
    <div 
      style={{ 
        width: '25px', 
        height: '25px', 
        borderRadius: '50%', 
        backgroundColor: '#000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      className={cn("", className)}>
      <svg
        width="30"
        height="30"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M9 12l2 2 4-4" />
      </svg>
    </div>
  );
};

const LoaderCore = ({
  loadingStates,
  value = 0
}) => {
  return (
    <div 
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: '28rem',
        margin: '0 auto',
        gap: '24px'
      }}>
      {loadingStates.map((loadingState, index) => {
        const distance = Math.abs(index - value);
        const opacity = Math.max(1 - distance * 0.2, 0);
        return (
          <motion.div
            key={index}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '12px',
              width: '100%'
            }}
            initial={{ opacity: 0, y: -(value * 40) }}
            animate={{ opacity: opacity, y: -(value * 40) }}
            transition={{ duration: 0.5 }}>
            <div style={{ flexShrink: 0 }}>
              {index > value && (
                <CheckIcon className="text-white" />
              )}
              {index <= value && (
                <CheckFilled
                  className={cn("text-white", value === index &&
                    "text-lime-400 opacity-100")} />
              )}
            </div>
            <span
              style={{
                color: index <= value ? 'black' : '#9ca3af',
                fontSize: '14px',
                fontWeight: 'normal',
                whiteSpace: 'nowrap'
              }}
              className={cn(
                value === index && "opacity-100"
              )}>
              {loadingState.text}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
};

export const MultiStepLoader = ({
  loadingStates,
  loading,
  duration = 3500,
  loop = true,
  startIndex = 0,
  loopStartIndex = null
}) => {
  const [currentState, setCurrentState] = useState(startIndex);

  useEffect(() => {
    if (!loading) {
      // Don't reset to 0, keep the current state
      return;
    }
    const timeout = setTimeout(() => {
      setCurrentState((prevState) => {
        if (loop) {
          if (prevState === loadingStates.length - 1) {
            // Instead of resetting to 0, continue from loopStartIndex or startIndex
            return loopStartIndex !== null ? loopStartIndex : startIndex;
          }
          return prevState + 1;
        }
        return Math.min(prevState + 1, loadingStates.length - 1);
      });
    }, duration);

    return () => clearTimeout(timeout);
  }, [currentState, loading, loop, loadingStates.length, duration, startIndex, loopStartIndex]);
  return (
    <AnimatePresence mode="wait">
      {loading && (
        <motion.div
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 999999,
            backgroundColor: '#fdf6ee',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <div 
            style={{
              textAlign: 'center',
              paddingTop: '60px'
            }}>
            <LoaderCore value={currentState} loadingStates={loadingStates} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
